import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "../Dropdown";
import type { Locale } from "../../i18n";
import { t } from "../../i18n";
import { getHostContext } from "../../hostContext";
import type { DateFormat, OpenMode } from "../../../config/ui";
import type { ThemeName } from "../../theme";

interface AppearanceSettingsProps {
  locale: Locale;
  theme: ThemeName;
  openMode: OpenMode;
  timezone: string;
  dateFormat: DateFormat;
  dataMode: "mock" | "api";
  onSetTheme: (theme: ThemeName) => void;
  onSetOpenMode: (mode: OpenMode) => void;
  onSetTimezone: (tz: string) => void;
  onSetDateFormat: (fmt: DateFormat) => void;
  onSetDataMode: (mode: "mock" | "api") => void;
}

const FIELD_NOTE: React.CSSProperties = {
  fontSize: "11px",
  color: "var(--muted)",
  marginTop: "4px"
};

const DIVIDER: React.CSSProperties = {
  height: "1px",
  background: "var(--line)",
  margin: "16px 0",
  opacity: 0.5
};

// --- Timezone helpers ---

type TzOption = { value: string; label: string; searchText: string };

function cityFromIana(iana: string): string {
  const parts = iana.split("/");
  const city = parts[parts.length - 1];
  return city.replace(/_/g, " ");
}

function utcOffsetLabel(iana: string): string {
  try {
    const now = new Date();
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: iana,
      timeZoneName: "shortOffset"
    });
    const parts = fmt.formatToParts(now);
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    if (tzPart) {
      // Comes back as "GMT+1", "GMT-5:30", "GMT" etc.
      const raw = tzPart.value.replace("GMT", "UTC");
      return raw === "UTC" ? "UTC+0" : raw;
    }
  } catch {
    // Fall through
  }
  return "";
}

function buildTimezoneOptions(): TzOption[] {
  const iana: string[] =
    typeof Intl !== "undefined" && "supportedValuesOf" in Intl
      ? (Intl as unknown as { supportedValuesOf(key: string): string[] }).supportedValuesOf("timeZone")
      : [];

  const autoOption: TzOption = {
    value: "auto",
    label: "Auto (match system)",
    searchText: "auto match system"
  };

  const ianaOptions: TzOption[] = iana.map((tz) => {
    const city = cityFromIana(tz);
    const offset = utcOffsetLabel(tz);
    const label = offset ? `${city} (${offset})` : city;
    return {
      value: tz,
      label,
      searchText: `${city} ${tz} ${offset}`.toLowerCase()
    };
  });

  return [autoOption, ...ianaOptions];
}

// --- Timezone Combobox ---

function TimezoneCombobox(props: {
  locale: Locale;
  value: string;
  onChange: (tz: string) => void;
}) {
  const allOptions = useMemo(() => buildTimezoneOptions(), []);

  const selectedLabel = useMemo(() => {
    const opt = allOptions.find((o) => o.value === props.value);
    return opt?.label ?? props.value;
  }, [allOptions, props.value]);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1);

  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!search) return allOptions;
    const q = search.toLowerCase();
    return allOptions.filter((o) => o.searchText.includes(q));
  }, [allOptions, search]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (wrapRef.current && e.target instanceof Node && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  // Scroll active item into view
  useEffect(() => {
    if (!open || activeIdx < 0) return;
    const list = listRef.current;
    if (!list) return;
    const item = list.children[activeIdx] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [activeIdx, open]);

  function handleOpen() {
    setOpen(true);
    setSearch("");
    setActiveIdx(-1);
    // Find current value in list and scroll to it
    setTimeout(() => {
      inputRef.current?.focus();
      const idx = allOptions.findIndex((o) => o.value === props.value);
      if (idx >= 0 && listRef.current) {
        const item = listRef.current.children[idx] as HTMLElement | undefined;
        item?.scrollIntoView({ block: "nearest" });
      }
    }, 0);
  }

  function select(value: string) {
    props.onChange(value);
    setOpen(false);
    setSearch("");
    setActiveIdx(-1);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0 && filtered[activeIdx]) {
      e.preventDefault();
      select(filtered[activeIdx].value);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "280px" }}>
      {/* Trigger button — styled to match the existing Dropdown component */}
      <button
        type="button"
        className="ddBtn"
        onClick={handleOpen}
        aria-label="Timezone"
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{ width: "100%" }}
      >
        <span className="ddLabel">{selectedLabel}</span>
        <span className="ddCaret">▼</span>
      </button>

      {open && (
        <div
          className="ddMenu"
          role="listbox"
          aria-label="Timezone"
          style={{ maxHeight: "260px", overflowY: "auto" }}
        >
          {/* Search input */}
          <div style={{ padding: "6px 8px", borderBottom: "1px solid var(--line)", position: "sticky", top: 0, background: "var(--surface)", zIndex: 1 }}>
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setActiveIdx(-1);
              }}
              onKeyDown={onKeyDown}
              placeholder={t(props.locale, "appearance.searchTimezones")}
              autoComplete="off"
              style={{
                width: "100%",
                padding: "5px 8px",
                border: "1px solid var(--control-border)",
                borderRadius: "4px",
                fontSize: "12px",
                color: "var(--text)",
                background: "var(--canvas)",
                outline: "none",
                boxSizing: "border-box"
              }}
            />
          </div>

          {/* Options list */}
          <div ref={listRef}>
            {filtered.length === 0 ? (
              <div style={{ padding: "10px 12px", color: "var(--muted)", fontSize: "12px" }}>
                {t(props.locale, "appearance.noTimezones")}
              </div>
            ) : (
              filtered.map((opt, i) => (
                <div
                  key={opt.value}
                  role="option"
                  aria-selected={opt.value === props.value}
                  className={`ddItem${opt.value === props.value ? " active" : ""}${i === activeIdx ? " active" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    select(opt.value);
                  }}
                  onMouseEnter={() => setActiveIdx(i)}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Main component ---

export function AppearanceSettings(props: AppearanceSettingsProps) {
  return (
    <div>
      <h2 style={{ marginTop: 0, marginBottom: "4px" }}>{t(props.locale, "appearance.title")}</h2>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: "24px" }}>
        {t(props.locale, "appearance.description", { brand: getHostContext().brandName })}
      </p>

      {/* Theme & Display Card */}
      <div style={{ background: "var(--canvas)", border: "1px solid var(--line)", borderRadius: "8px", padding: "20px", marginBottom: "20px" }}>
        <h3 style={{ marginTop: 0, fontSize: "15px", fontWeight: 600, marginBottom: "16px" }}>{t(props.locale, "appearance.themeDisplay")}</h3>

        {/* Theme */}
        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: "8px", fontSize: "13px" }}>
            {t(props.locale, "settings.theme")}
          </label>
          <Dropdown
            ariaLabel="Theme"
            value={props.theme}
            widthPx={200}
            options={[
              { value: "light", label: "Light" },
              { value: "slate", label: "Slate" },
              { value: "nord", label: "Nord" }
            ]}
            onChange={(v) => props.onSetTheme(v)}
          />
          <div style={FIELD_NOTE}>{t(props.locale, "appearance.deviceOnly")}</div>
        </div>

        <div style={DIVIDER} />

        {/* Open mode */}
        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: "8px", fontSize: "13px" }}>
            {t(props.locale, "settings.openMode")}
          </label>
          <Dropdown
            ariaLabel="Open mode"
            value={props.openMode}
            widthPx={240}
            options={[
              { value: "in-list", label: "Open mail in listing" },
              { value: "page", label: "Open in new page" },
              { value: "window", label: "Open in new window" }
            ]}
            onChange={(v) => props.onSetOpenMode(v)}
          />
          <div style={FIELD_NOTE}>{t(props.locale, "appearance.deviceOnly")}</div>
        </div>

        <div style={DIVIDER} />

        {/* Timezone */}
        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: "8px", fontSize: "13px" }}>
            {t(props.locale, "appearance.timezone")}
          </label>
          <TimezoneCombobox
            locale={props.locale}
            value={props.timezone}
            onChange={props.onSetTimezone}
          />
          <div style={FIELD_NOTE}>{t(props.locale, "appearance.deviceOnly")}</div>
        </div>

        <div style={DIVIDER} />

        {/* Date format */}
        <div style={{ marginBottom: "0" }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: "8px", fontSize: "13px" }}>
            {t(props.locale, "appearance.dateFormat")}
          </label>
          <Dropdown
            ariaLabel="Date and time format"
            value={props.dateFormat}
            widthPx={280}
            options={[
              { value: "auto", label: "Auto (match system)" },
              { value: "iso", label: "ISO 24h  —  2026-04-30 14:30" },
              { value: "long", label: "Long 12h  —  April 30, 2026 2:30 PM" },
              { value: "us", label: "US  —  04/30/2026 2:30 PM" },
              { value: "eu", label: "EU  —  30/04/2026 14:30" }
            ]}
            onChange={(v) => props.onSetDateFormat(v)}
          />
          <div style={FIELD_NOTE}>{t(props.locale, "appearance.syncedAll")}</div>
        </div>
      </div>

      {/* Auto-save note */}
      <div style={{ fontSize: "12px", color: "var(--muted)", marginTop: "4px" }}>
        {t(props.locale, "appearance.autoSaved")}
      </div>
    </div>
  );
}
